// CHG-123 REQ-V123-033/034: 메시지 → 이슈 생성 폼 (CHG 전용 — 기존 IssueCreateModal 부재로 신규 작성)
import { useState, useEffect, useMemo } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatMessage } from '../../services/chatService';
import { createIssueFromMessage } from '../../services/issueService';
import { issueKeys } from '../../hooks/useIssues';
import Spinner from '../ui/Spinner';

interface IssueCreateFromMessageModalProps {
  open: boolean;
  message: ChatMessage;
  projectId: string;
  projectKey: string;
  issueTypeId: string;
  issueTypeName: string;
  onClose: () => void;
  onBack: () => void;
}

const PRIORITIES: Array<'BLOCKER' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'> = [
  'BLOCKER', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW',
];

/** REQ-V123-012: 메시지 첫 줄 → title (300자 절단) */
function extractTitle(content: string): string {
  const first = (content ?? '').split(/\r?\n/)[0]?.trim() ?? '';
  if (first.length === 0) return '채팅 메시지에서 생성됨';
  if (first.length > 300) return first.slice(0, 297) + '…';
  return first;
}

/** REQ-V123-033: description 인용 형식 prefill */
function buildDescription(message: ChatMessage): string {
  const senderName = message.sender?.name ?? '알 수 없음';
  const d = new Date(message.createdAt);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const dateStr = `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  const quotedBody = (message.content ?? '').split(/\r?\n/).map((line) => `> ${line}`).join('\n');
  return `> ${senderName} (${dateStr})에서 인용:\n${quotedBody}\n`;
}

export default function IssueCreateFromMessageModal({
  open, message, projectId, projectKey, issueTypeId, issueTypeName,
  onClose, onBack,
}: IssueCreateFromMessageModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const initialTitle = useMemo(() => extractTitle(message.content), [message.content]);
  const initialDescription = useMemo(() => buildDescription(message), [message]);

  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [priority, setPriority] = useState<'BLOCKER' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'>('MEDIUM');
  const [submitting, setSubmitting] = useState(false);

  // 모달이 열릴 때마다 prefill 재설정 (다른 메시지 / 다른 이슈타입으로 진입할 수 있으므로)
  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setDescription(initialDescription);
      setPriority('MEDIUM');
    }
  }, [open, initialTitle, initialDescription]);

  // ESC로 닫기
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    try {
      const issue = await createIssueFromMessage(projectId, {
        sourceMessageId: message.messageId,
        title: title.trim(),
        description: description || undefined,
        issueTypeId,
        priority,
      });
      // 토스트 + 이슈키 클릭 시 이슈 상세 이동
      toast.success(
        (t) => (
          <span className="flex items-center gap-2">
            <span>작업이 생성되었습니다 —</span>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                navigate(`/projects/${projectKey}/issues?panel=${issue.id}`);
              }}
              className="font-semibold text-primary-600 underline"
            >
              {issue.issueId}
            </button>
          </span>
        ),
        { duration: 6000 },
      );
      // 메시지 응답 갱신 (linkedResources용 — Step 6에서 표시)
      queryClient.invalidateQueries({ queryKey: ['chatMessages'] });
      queryClient.invalidateQueries({ queryKey: issueKeys.all(projectId) });
      onClose();
    } catch (err) {
      const errMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '작업 생성에 실패했습니다.';
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="issue-create-from-msg-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="p-1 text-gray-400 hover:text-gray-600 rounded" aria-label="뒤로">
              <ArrowLeft className="size-5" />
            </button>
            <div>
              <h3 id="issue-create-from-msg-title" className="text-lg font-semibold text-gray-900">작업 생성</h3>
              <p className="text-sm text-gray-500">{projectKey} · {issueTypeName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded" aria-label="닫기">
            <X className="size-5" />
          </button>
        </div>

        {/* 폼 */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={300}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">우선순위</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as typeof priority)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500"
            >
              {PRIORITIES.map((p) => (<option key={p} value={p}>{p}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={50000}
              rows={8}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500 resize-y font-mono"
            />
            <p className="mt-1 text-sm text-gray-400">메시지가 인용 형식으로 미리 채워져 있습니다. 자유롭게 수정 가능합니다.</p>
          </div>
        </div>

        {/* 푸터 */}
        <div className="flex justify-end gap-3 mt-4 shrink-0">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || submitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {submitting && <Spinner className="size-4 text-white" />}
            {submitting ? '생성 중...' : '생성'}
          </button>
        </div>
      </div>
    </div>
  );
}
